import * as THREE from "three";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { getSceneState } from "../../stores/sceneStore";
import { getVertexTransformFromMjcfMeshRef } from "../../viewer/mjcfMeshReferenceTransform";

export function normalizeOriginVector3(value) {
  return Array.isArray(value) && value.length === 3
    ? value.map((entry) => {
        const n = Number(entry);
        return Number.isFinite(n) ? n : 0;
      })
    : null;
}

export function normalizeOriginQuaternion(value) {
  return Array.isArray(value) && value.length === 4
    ? value.map((entry, index) => {
        const n = Number(entry);
        return Number.isFinite(n) ? n : index === 0 ? 1 : 0;
      })
    : [1, 0, 0, 0];
}

export function toOriginThreeQuaternion(value) {
  const quat = normalizeOriginQuaternion(value);
  return new THREE.Quaternion(quat[1] || 0, quat[2] || 0, quat[3] || 0, quat[0] || 1).normalize();
}

export function fromOriginThreeQuaternion(quaternion) {
  const normalized = quaternion.clone().normalize();
  return [normalized.w, normalized.x, normalized.y, normalized.z];
}

export function findTransformLayerFromObject(object, transformLayer) {
  let current = object;
  while (current) {
    if (current.userData?.transformLayer === transformLayer) return current;
    current = current.parent;
  }
  return null;
}

export function findTransformLayerInSubtree(object, transformLayer) {
  let found = null;
  if (!object || typeof object.traverse !== "function") return null;
  object.traverse((child) => {
    if (!found && child.userData?.transformLayer === transformLayer) found = child;
  });
  return found;
}

export function computeMeshGeometryWorldBounds(root) {
  if (!root) return null;
  const bounds = new THREE.Box3();
  let hasGeometry = false;
  root.updateWorldMatrix(true, true);
  root.traverse((child) => {
    if (!child?.isMesh || !child.geometry) return;
    const childBounds = new THREE.Box3().setFromObject(child);
    if (childBounds.isEmpty()) return;
    bounds.union(childBounds);
    hasGeometry = true;
  });
  return hasGeometry ? bounds : null;
}

function unionBoxInObjectSpace(
  targetBounds,
  sourceBox,
  sourceMatrixWorld,
  spaceMatrixWorldInverse
) {
  const points = [
    [sourceBox.min.x, sourceBox.min.y, sourceBox.min.z],
    [sourceBox.max.x, sourceBox.min.y, sourceBox.min.z],
    [sourceBox.min.x, sourceBox.max.y, sourceBox.min.z],
    [sourceBox.max.x, sourceBox.max.y, sourceBox.min.z],
    [sourceBox.min.x, sourceBox.min.y, sourceBox.max.z],
    [sourceBox.max.x, sourceBox.min.y, sourceBox.max.z],
    [sourceBox.min.x, sourceBox.max.y, sourceBox.max.z],
    [sourceBox.max.x, sourceBox.max.y, sourceBox.max.z],
  ];
  points.forEach(([x, y, z]) => {
    targetBounds.expandByPoint(
      new THREE.Vector3(x, y, z)
        .applyMatrix4(sourceMatrixWorld)
        .applyMatrix4(spaceMatrixWorldInverse)
    );
  });
}

export function computeMeshGeometryBoundsInObjectSpace(root, spaceObject) {
  if (!root || !spaceObject) return null;
  const bounds = new THREE.Box3();
  let hasGeometry = false;
  root.updateWorldMatrix(true, true);
  spaceObject.updateWorldMatrix(true, false);
  const inverseSpaceMatrixWorld = spaceObject.matrixWorld.clone().invert();
  root.traverse((child) => {
    if (!child?.isMesh || !child.geometry) return;
    if (!child.geometry.boundingBox) child.geometry.computeBoundingBox();
    if (!child.geometry.boundingBox || child.geometry.boundingBox.isEmpty()) return;
    child.updateWorldMatrix(true, false);
    unionBoxInObjectSpace(
      bounds,
      child.geometry.boundingBox,
      child.matrixWorld,
      inverseSpaceMatrixWorld
    );
    hasGeometry = true;
  });
  return hasGeometry ? bounds : null;
}

export function closestPointOnBoundsToOrigin(bounds) {
  if (!bounds || bounds.isEmpty()) return null;
  const clampAxis = (min, max) => {
    if (0 < min) return min;
    if (0 > max) return max;
    return 0;
  };
  return [
    clampAxis(bounds.min.x, bounds.max.x),
    clampAxis(bounds.min.y, bounds.max.y),
    clampAxis(bounds.min.z, bounds.max.z),
  ];
}

export function findVisualMeshObject({ bodyUuid, geomUuid, meshUuid = null }) {
  if (!bodyUuid || !geomUuid) return null;
  try {
    const { scene } = getViewerHandles();
    if (!scene) return null;
    let found = null;
    scene.traverse((obj) => {
      const userData = obj?.userData || {};
      if (found) return;
      if (userData.type !== "visual" && userData.transformLayer !== "outer") return;
      if (String(userData.bodyUuid || "") !== String(bodyUuid)) return;
      if (String(userData.geomUuid || "") !== String(geomUuid)) return;
      if (meshUuid && String(userData.meshName || userData.meshUuid || "") !== String(meshUuid)) {
        return;
      }
      found = obj;
    });
    return found;
  } catch {
    return null;
  }
}

export function getVisibleMeshCenterOriginLocal({ bodyUuid, geomUuid, meshUuid }) {
  const visualObject = findVisualMeshObject({ bodyUuid, geomUuid, meshUuid });
  const refGroup =
    findTransformLayerFromObject(visualObject, "ref") ||
    findTransformLayerInSubtree(visualObject, "ref");
  const refParent = refGroup?.parent || null;
  if (!refGroup || !refParent) return null;
  refParent.updateMatrixWorld(true);
  refGroup.updateMatrixWorld(true);
  const bounds = computeMeshGeometryWorldBounds(refGroup);
  if (!bounds || bounds.isEmpty()) return null;
  const center = bounds.getCenter(new THREE.Vector3());
  refParent.worldToLocal(center);
  return [center.x, center.y, center.z];
}

export function getVisibleMeshStartOriginLocal({ bodyUuid, geomUuid, meshUuid }) {
  const visualObject = findVisualMeshObject({ bodyUuid, geomUuid, meshUuid });
  const refGroup =
    findTransformLayerFromObject(visualObject, "ref") ||
    findTransformLayerInSubtree(visualObject, "ref");
  const refParent = refGroup?.parent || null;
  if (!refGroup || !refParent) return null;
  const bounds = computeMeshGeometryBoundsInObjectSpace(refGroup, refParent);
  return closestPointOnBoundsToOrigin(bounds);
}

export function syncMeshOriginSceneTransforms(
  meshUuid,
  referencePosition,
  referenceOrientation,
  geomPositionsByKey,
  geomOrientationsByKey = new Map()
) {
  try {
    const { scene } = getViewerHandles();
    if (!scene || !meshUuid) return;
    const { position: refPosition, quat: refQuaternion } = getVertexTransformFromMjcfMeshRef({
      referencePosition,
      referenceOrientation,
    });
    const geomPositionsByGeomUuid = new Map();
    geomPositionsByKey.forEach((position, key) => {
      const sep = String(key).indexOf(":");
      const geomUuid = sep >= 0 ? String(key).slice(sep + 1) : String(key);
      if (geomUuid) geomPositionsByGeomUuid.set(geomUuid, position);
    });
    const geomOrientationsByGeomUuid = new Map();
    geomOrientationsByKey.forEach((orientation, key) => {
      const sep = String(key).indexOf(":");
      const geomUuid = sep >= 0 ? String(key).slice(sep + 1) : String(key);
      if (geomUuid) geomOrientationsByGeomUuid.set(geomUuid, orientation);
    });
    scene.traverse((obj) => {
      const ud = obj.userData || {};
      if (String(ud.meshName || "") !== String(meshUuid)) return;
      if (ud.transformLayer === "ref") {
        obj.position.copy(refPosition);
        obj.quaternion.copy(refQuaternion);
      } else if (ud.transformLayer === "outer") {
        const key = `${ud.bodyUuid || ""}:${ud.geomUuid || ""}`;
        const nextPosition =
          geomPositionsByKey.get(key) || geomPositionsByGeomUuid.get(String(ud.geomUuid || ""));
        if (nextPosition) {
          obj.position.set(nextPosition[0] || 0, nextPosition[1] || 0, nextPosition[2] || 0);
        }
      } else if (ud.transformLayer === "geom") {
        const key = `${ud.bodyUuid || ""}:${ud.geomUuid || ""}`;
        const nextOrientation =
          geomOrientationsByKey.get(key) ||
          geomOrientationsByGeomUuid.get(String(ud.geomUuid || ""));
        if (nextOrientation) {
          obj.quaternion.set(
            nextOrientation[1] || 0,
            nextOrientation[2] || 0,
            nextOrientation[3] || 0,
            Number.isFinite(Number(nextOrientation[0])) ? nextOrientation[0] : 1
          );
          obj.quaternion.normalize();
        }
      }
    });
    scene.updateMatrixWorld(true);
  } catch {
    // Best-effort live scene sync; store state and rebuild remain authoritative.
  }
}

export function moveMeshOriginToLocalPointPreservingInstances({
  meshUuid,
  sceneActions,
  targetOriginLocalInput,
  selectedBodyUuid = null,
  selectedGeomUuid = null,
}) {
  if (!meshUuid || !sceneActions) return null;
  const targetOriginLocal = normalizeOriginVector3(targetOriginLocalInput);
  if (!targetOriginLocal) return null;

  const sceneSnapshot = getSceneState();
  const meshData = sceneSnapshot.meshes?.[meshUuid] || null;
  const currentReferencePosition = normalizeOriginVector3(meshData?.referencePosition) || [0, 0, 0];
  const referenceOrientation = normalizeOriginQuaternion(meshData?.referenceOrientation);
  const localDelta = new THREE.Vector3(
    targetOriginLocal[0],
    targetOriginLocal[1],
    targetOriginLocal[2]
  );
  const rawDelta = localDelta
    .clone()
    .applyQuaternion(toOriginThreeQuaternion(referenceOrientation));
  const nextReferencePosition = [
    currentReferencePosition[0] + rawDelta.x,
    currentReferencePosition[1] + rawDelta.y,
    currentReferencePosition[2] + rawDelta.z,
  ];

  let selectedNextPosition = null;
  const geomPositionsByKey = new Map();
  Object.entries(sceneSnapshot.bodies || {}).forEach(([candidateBodyUuid, candidateBody]) => {
    const geoms = Array.isArray(candidateBody?.geoms) ? candidateBody.geoms : [];
    geoms.forEach((candidateGeom) => {
      if (!candidateGeom?.uuid || String(candidateGeom.mesh || "") !== String(meshUuid)) return;
      const currentGeomPosition = normalizeOriginVector3(
        candidateGeom.position || candidateGeom.pos
      ) || [0, 0, 0];
      const geomQuaternion = toOriginThreeQuaternion(
        candidateGeom.orientation || candidateGeom.quat || [1, 0, 0, 0]
      );
      const adjustedDelta = localDelta.clone().applyQuaternion(geomQuaternion);
      const nextGeomPosition = [
        currentGeomPosition[0] + adjustedDelta.x,
        currentGeomPosition[1] + adjustedDelta.y,
        currentGeomPosition[2] + adjustedDelta.z,
      ];
      const geomKey = `${candidateBodyUuid}:${candidateGeom.uuid}`;
      geomPositionsByKey.set(geomKey, nextGeomPosition);
      sceneActions.setGeomPosition(candidateBodyUuid, geomKey, nextGeomPosition);
      if (
        String(candidateBodyUuid) === String(selectedBodyUuid) &&
        String(candidateGeom.uuid) === String(selectedGeomUuid)
      ) {
        selectedNextPosition = nextGeomPosition;
      }
    });
  });

  sceneActions.setMeshReferenceTransform(meshUuid, nextReferencePosition, referenceOrientation);
  syncMeshOriginSceneTransforms(
    meshUuid,
    nextReferencePosition,
    referenceOrientation,
    geomPositionsByKey
  );

  return {
    geomPositionsByKey,
    nextReferencePosition,
    selectedNextPosition,
  };
}

export function setMeshOriginPositionPreservingInstances({
  meshUuid,
  nextOriginPositionInput,
  sceneActions,
  selectedBodyUuid = null,
  selectedGeomUuid = null,
}) {
  const nextOriginPosition = normalizeOriginVector3(nextOriginPositionInput);
  if (!nextOriginPosition) return null;
  const meshData = getSceneState().meshes?.[meshUuid] || null;
  const currentReferencePosition = normalizeOriginVector3(meshData?.referencePosition) || [0, 0, 0];
  const referenceOrientation = normalizeOriginQuaternion(meshData?.referenceOrientation);
  const rawDelta = new THREE.Vector3(
    nextOriginPosition[0] - currentReferencePosition[0],
    nextOriginPosition[1] - currentReferencePosition[1],
    nextOriginPosition[2] - currentReferencePosition[2]
  );
  const localDelta = rawDelta.applyQuaternion(
    toOriginThreeQuaternion(referenceOrientation).invert()
  );
  return moveMeshOriginToLocalPointPreservingInstances({
    meshUuid,
    sceneActions,
    selectedBodyUuid,
    selectedGeomUuid,
    targetOriginLocalInput: [localDelta.x, localDelta.y, localDelta.z],
  });
}

export function setMeshOriginTransformPreservingInstances({
  meshUuid,
  nextOriginPositionInput,
  nextOriginOrientationInput,
  sceneActions,
  selectedBodyUuid = null,
  selectedGeomUuid = null,
}) {
  if (!meshUuid || !sceneActions) return null;
  const nextReferencePosition = normalizeOriginVector3(nextOriginPositionInput);
  if (!nextReferencePosition) return null;
  const nextReferenceOrientation = normalizeOriginQuaternion(nextOriginOrientationInput);

  const sceneSnapshot = getSceneState();
  const meshData = sceneSnapshot.meshes?.[meshUuid] || null;
  const currentReferencePosition = normalizeOriginVector3(meshData?.referencePosition) || [0, 0, 0];
  const currentReferenceOrientation = normalizeOriginQuaternion(meshData?.referenceOrientation);
  const currentVertexTransform = getVertexTransformFromMjcfMeshRef({
    referencePosition: currentReferencePosition,
    referenceOrientation: currentReferenceOrientation,
  });
  const nextVertexTransform = getVertexTransformFromMjcfMeshRef({
    referencePosition: nextReferencePosition,
    referenceOrientation: nextReferenceOrientation,
  });
  const inverseNextRefQuaternion = nextVertexTransform.quat.clone().invert();

  let selectedNextPosition = null;
  const geomPositionsByKey = new Map();
  const geomOrientationsByKey = new Map();
  Object.entries(sceneSnapshot.bodies || {}).forEach(([candidateBodyUuid, candidateBody]) => {
    const geoms = Array.isArray(candidateBody?.geoms) ? candidateBody.geoms : [];
    geoms.forEach((candidateGeom) => {
      if (!candidateGeom?.uuid || String(candidateGeom.mesh || "") !== String(meshUuid)) return;
      const currentGeomPosition = normalizeOriginVector3(
        candidateGeom.position || candidateGeom.pos
      ) || [0, 0, 0];
      const currentGeomQuaternion = toOriginThreeQuaternion(
        candidateGeom.orientation || candidateGeom.quat || [1, 0, 0, 0]
      );
      const nextGeomQuaternion = currentGeomQuaternion
        .clone()
        .multiply(currentVertexTransform.quat)
        .multiply(inverseNextRefQuaternion)
        .normalize();
      const currentRefOffset = currentVertexTransform.position
        .clone()
        .applyQuaternion(currentGeomQuaternion);
      const nextRefOffset = nextVertexTransform.position
        .clone()
        .applyQuaternion(nextGeomQuaternion);
      const nextGeomPositionVector = new THREE.Vector3(
        currentGeomPosition[0],
        currentGeomPosition[1],
        currentGeomPosition[2]
      )
        .add(currentRefOffset)
        .sub(nextRefOffset);
      const nextGeomPosition = [
        nextGeomPositionVector.x,
        nextGeomPositionVector.y,
        nextGeomPositionVector.z,
      ];
      const nextGeomOrientation = fromOriginThreeQuaternion(nextGeomQuaternion);
      const geomKey = `${candidateBodyUuid}:${candidateGeom.uuid}`;
      geomPositionsByKey.set(geomKey, nextGeomPosition);
      geomOrientationsByKey.set(geomKey, nextGeomOrientation);
      sceneActions.setGeomPosition(candidateBodyUuid, geomKey, nextGeomPosition);
      sceneActions.setGeomOrientation(candidateBodyUuid, geomKey, nextGeomOrientation);
      if (
        String(candidateBodyUuid) === String(selectedBodyUuid) &&
        String(candidateGeom.uuid) === String(selectedGeomUuid)
      ) {
        selectedNextPosition = nextGeomPosition;
      }
    });
  });

  sceneActions.setMeshReferenceTransform(meshUuid, nextReferencePosition, nextReferenceOrientation);
  syncMeshOriginSceneTransforms(
    meshUuid,
    nextReferencePosition,
    nextReferenceOrientation,
    geomPositionsByKey,
    geomOrientationsByKey
  );

  return {
    geomOrientationsByKey,
    geomPositionsByKey,
    nextReferenceOrientation,
    nextReferencePosition,
    selectedNextPosition,
  };
}
